import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClientMutator } from '~/infrastructure/config/mutator';
import { CzMaxPrice, ProductUpdate } from '~/libs/infrastructure/schemas/czMaxPrice';
import { mapCzMaxPrice, mapReferenceBasketVersion } from '~/libs/infrastructure/mappers/czMaxPrice';
import ReferenceBasketVersion from '~/Api/PriceReferencing/ReferenceBasketVersion';
import {
	CzechMaxPricePriceReferencingReferenceBasketVersionsResponse,
	CzechMaxPriceWrappedCalculationSettingsResponse,
	WrappedExportResponse,
} from 'com.cogvio.pricemonitor.api';

export const STALE_TIME = 5 * 60 * 1000;

export function getCzMaxPriceKey(groupId: string) {
	return ['czMaxPrice', groupId] as const;
}

export function getCzMaxPriceReferenceBasketVersionsKey() {
	return ['czMaxPrice', 'referenceBasketVersions'] as const;
}

export function getCzMaxPrice(userId: string, groupId: string) {
	return apiClientMutator<CzechMaxPriceWrappedCalculationSettingsResponse>({
		url: `/users/${userId}/product-groups/${groupId}/cz-max-price`,
		method: 'get',
	}).then((data) => mapCzMaxPrice(data.czMaxPrice));
}

export function postCzMaxPriceUpdate(
	userId: string,
	groupId: string,
	packageSize: number | null,
	referenceBasketVersion: string,
	products: ProductUpdate[]
) {
	return apiClientMutator<CzechMaxPriceWrappedCalculationSettingsResponse>({
		url: `/users/${userId}/product-groups/${groupId}/cz-max-price`,
		method: 'post',
		data: JSON.stringify({
			reference: { packageSize, referenceBasketVersion },
			products: products.map((product) => ({
				id: product.id,
				priceTypeForCalculation: product.priceTypeForCalculation,
				packageSizeForCalculation: product.packageSizeForCalculation,
				packageSizeForSingleUnit: product.packageSizeForSingleUnit,
				selected: product.selected,
			})),
		}),
	}).then((data) => mapCzMaxPrice(data.czMaxPrice));
}

export function postCzMaxPriceExportXls(userId: string, groupId: string) {
	return apiClientMutator<WrappedExportResponse>({
		url: `/users/${userId}/product-groups/${groupId}/cz-max-price/export-xls`,
		method: 'post',
	});
}

export function getCzMaxPriceReferenceBasketVersions() {
	return apiClientMutator<CzechMaxPricePriceReferencingReferenceBasketVersionsResponse>({
		url: '/cz-price-referencing/max-price/reference-basket-versions',
		method: 'get',
	}).then((data) => data.items.map(mapReferenceBasketVersion));
}

export function useCzMaxPriceData(userId: string, groupId: string) {
	return useQuery<CzMaxPrice>({
		queryKey: getCzMaxPriceKey(groupId),
		queryFn: () => getCzMaxPrice(userId, groupId),
		staleTime: STALE_TIME,
	});
}

export function useCzMaxPriceReferenceBasketVersions() {
	return useQuery<ReferenceBasketVersion[]>({
		queryKey: getCzMaxPriceReferenceBasketVersionsKey(),
		queryFn: getCzMaxPriceReferenceBasketVersions,
		staleTime: STALE_TIME,
	});
}

export function useCzMaxPriceUpdate(
	userId: string,
	groupId: string,
	options: {
		onMutate?: () => void;
		onSuccess?: (data: CzMaxPrice) => void;
		onError?: () => void;
	} = {}
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (params: { packageSize: number | null; basketVersion: string; products: ProductUpdate[] }) =>
			postCzMaxPriceUpdate(userId, groupId, params.packageSize, params.basketVersion, params.products),
		onMutate: options.onMutate,
		onSuccess: (data) => {
			queryClient.setQueryData(getCzMaxPriceKey(groupId), data);
			options.onSuccess?.(data);
		},
		onError: options.onError,
	});
}
